import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { normalizePartyEntityKind, type PartyEntityKind } from "@/lib/parties/entityKind";
import { normalizePartyType } from "@/lib/parties/partyTypes";
import { isAxiosError } from "axios";

export type InvestorPartyType = "all" | "investor";

export interface InvestorRecord {
  id: string;
  name: string;
  fatherName: string;
  cnic: string;
  ntn: string;
  contactPerson: string;
  country: number | null;
  countryName: string;
  state: number | null;
  stateName: string;
  city: string;
  postalCode: string;
  addressLine1: string;
  addressLine2: string;
  /** Combined address_line_1 + address_line_2 for search/display convenience. */
  address: string;
  phone: string;
  email: string;
  type: InvestorPartyType;
  entityKind: PartyEntityKind;
  createdAt: string;
  updatedAt: string;
}

export type InvestorDraft = Pick<
  InvestorRecord,
  "name" | "fatherName" | "cnic" | "ntn" | "contactPerson" | "phone" | "email" | "city" | "type" | "entityKind"
> & {
  country: number | null;
  state: number | null;
  postalCode: string;
  addressLine1: string;
  addressLine2: string;
};

const LS_KEY = "inventory.investors.v3";

type InvestorApiRecord = {
  id?: number | string;
  name: string;
  father_name?: string | null;
  cnic?: string | null;
  ntn?: string | null;
  contact_person?: string | null;
  country?: number | string | null;
  country_name?: string | null;
  state?: number | string | null;
  state_name?: string | null;
  city?: string | null;
  postal_code?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  type?: string | null;
  entity_kind?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertInvestorApiResponse = {
  message?: string;
  data?: InvestorApiRecord;
};

type DeleteInvestorApiResponse = {
  message?: string;
};

type UpsertInvestorResult = {
  record: InvestorRecord;
  message: string;
};

type DeleteInvestorResult = {
  success: boolean;
  message: string;
};

export const emptyInvestorDraft = (): InvestorDraft => ({
  name: "",
  fatherName: "",
  cnic: "",
  ntn: "",
  contactPerson: "",
  phone: "",
  email: "",
  type: "investor",
  entityKind: "individual",
  country: null,
  state: null,
  city: "",
  postalCode: "",
  addressLine1: "",
  addressLine2: "",
});

export function formatInvestorDisplayName(
  investor: Pick<InvestorRecord, "name" | "fatherName" | "entityKind">,
): string {
  const name = investor.name.trim();
  if (normalizePartyEntityKind(investor.entityKind) === "business") {
    return name || "—";
  }
  const father = investor.fatherName.trim();
  if (!name) return father || "—";
  if (!father) return name;
  return `${name} (S/O ${father})`;
}

export function investorErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

function dedupeById(items: InvestorRecord[]): InvestorRecord[] {
  const byId = new Map<ID, InvestorRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function extractRowsFromResponse(body: unknown): InvestorApiRecord[] {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;

  const fromBlock = (block: unknown): InvestorApiRecord[] | null => {
    if (Array.isArray(block)) return block as InvestorApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as InvestorApiRecord[];
    }
    return null;
  };

  for (const key of ["data", "investors", "items"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function mapApiRecord(record: InvestorApiRecord): InvestorRecord {
  const created = record.created_at || new Date().toISOString();
  const line1 = record.address_line_1 ?? record.address ?? "";
  const line2 = record.address_line_2 ?? "";
  const combined = [line1, line2].map((s) => s.trim()).filter(Boolean).join(", ");
  return {
    id: String(record.id ?? ""),
    name: record.name,
    fatherName: record.father_name ?? "",
    cnic: record.cnic ?? "",
    ntn: record.ntn ?? "",
    contactPerson: record.contact_person ?? "",
    country: record.country != null && record.country !== "" ? Number(record.country) : null,
    countryName: record.country_name ?? "",
    state: record.state != null && record.state !== "" ? Number(record.state) : null,
    stateName: record.state_name ?? "",
    city: record.city ?? "",
    postalCode: record.postal_code ?? "",
    addressLine1: line1,
    addressLine2: line2,
    address: combined,
    phone: record.phone ?? "",
    email: record.email ?? "",
    type: normalizePartyType(record.type, "investor") as InvestorPartyType,
    entityKind: normalizePartyEntityKind(record.entity_kind),
    createdAt: created,
    updatedAt: record.updated_at ?? created,
  };
}

export function normalizeInvestorDraft(draft: InvestorDraft): InvestorDraft {
  return {
    name: draft.name.trim(),
    fatherName: draft.fatherName?.trim() ?? "",
    cnic: draft.cnic?.trim() ?? "",
    ntn: draft.ntn?.trim() ?? "",
    contactPerson: draft.contactPerson?.trim() ?? "",
    phone: draft.phone?.trim() ?? "",
    email: draft.email?.trim() ?? "",
    type: normalizePartyType(draft.type, "investor") as InvestorPartyType,
    entityKind: normalizePartyEntityKind(draft.entityKind),
    country: draft.country ?? null,
    state: draft.state ?? null,
    city: draft.city?.trim() ?? "",
    postalCode: draft.postalCode?.trim() ?? "",
    addressLine1: draft.addressLine1?.trim() ?? "",
    addressLine2: draft.addressLine2?.trim() ?? "",
  };
}

export type InvestorDraftValidation = {
  valid: boolean;
  message?: string;
};

export function validateInvestorDraft(draft: InvestorDraft): InvestorDraftValidation {
  const n = normalizeInvestorDraft(draft);
  if (!n.name) {
    return {
      valid: false,
      message: n.entityKind === "business" ? "Business name is required." : "Investor name is required.",
    };
  }
  if (!n.phone) return { valid: false, message: "Contact number is required." };
  if (!n.country) return { valid: false, message: "Country is required." };
  if (!n.state) return { valid: false, message: "State is required." };
  if (!n.city) return { valid: false, message: "City is required." };
  return { valid: true };
}

export function isInvestorDraftComplete(draft: InvestorDraft): boolean {
  return validateInvestorDraft(draft).valid;
}

function buildApiPayload(draft: InvestorDraft) {
  const n = normalizeInvestorDraft(draft);
  return {
    name: n.name,
    father_name: n.fatherName || null,
    cnic: n.cnic || null,
    ntn: n.ntn || null,
    contact_person: n.contactPerson || null,
    phone: n.phone || null,
    email: n.email || null,
    type: n.type,
    entity_kind: n.entityKind,
    country: n.country,
    state: n.state,
    city: n.city || null,
    postal_code: n.postalCode || null,
    address_line_1: n.addressLine1 || null,
    address_line_2: n.addressLine2 || null,
  };
}

function load(): InvestorRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as InvestorRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: InvestorRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listInvestors(): InvestorRecord[] {
  return load().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function getInvestorById(id: ID): InvestorRecord | undefined {
  return load().find((c) => c.id === id);
}

export async function syncInvestorsFromApi() {
  try {
    const response = await api.get("/api/v1/investors", {
      params: { page: 1, per_page: 200 },
    });
    const rows = extractRowsFromResponse(response.data);
    const records = rows.map(mapApiRecord).filter((r) => Boolean(r.id));
    save(records);
    return listInvestors();
  } catch (error) {
    if (!(isAxiosError(error) && error.response?.status === 403)) {
      console.error("[Investors] syncInvestorsFromApi failed", error);
    }
    throw error;
  }
}

export async function createInvestor(draft: InvestorDraft): Promise<UpsertInvestorResult> {
  const items = load();
  const payload = buildApiPayload(draft);
  const response = await api.post<UpsertInvestorApiResponse>("/api/v1/investors", payload);
  const created = response.data?.data;
  if (!created || created.id == null) {
    throw new Error("Invalid create investor response.");
  }
  const record = mapApiRecord(created);
  const idx = items.findIndex((c) => c.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Investor created successfully." };
}

export async function updateInvestor(id: ID, draft: InvestorDraft): Promise<UpsertInvestorResult | null> {
  const items = load();
  const idx = items.findIndex((c) => c.id === id);
  if (idx === -1) return null;
  const payload = buildApiPayload(draft);
  const response = await api.put<UpsertInvestorApiResponse>(`/api/v1/investors/${id}`, payload);
  const updated = response.data?.data;
  if (!updated) {
    throw new Error("Invalid update investor response.");
  }
  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Investor updated successfully." };
}

export async function deleteInvestor(id: ID): Promise<DeleteInvestorResult> {
  const response = await api.delete<DeleteInvestorApiResponse>(`/api/v1/investors/${id}`);
  const items = load();
  const next = items.filter((c) => c.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return { success: removed, message: response.data?.message || "Investor deleted successfully." };
}

export function recordToInvestorDraft(c: InvestorRecord): InvestorDraft {
  return {
    name: c.name,
    fatherName: c.fatherName ?? "",
    cnic: c.cnic ?? "",
    ntn: c.ntn ?? "",
    contactPerson: c.contactPerson ?? "",
    phone: c.phone ?? "",
    email: c.email ?? "",
    type: normalizePartyType(c.type, "investor") as InvestorPartyType,
    entityKind: normalizePartyEntityKind(c.entityKind),
    country: c.country ?? null,
    state: c.state ?? null,
    city: c.city ?? "",
    postalCode: c.postalCode ?? "",
    addressLine1: c.addressLine1 ?? "",
    addressLine2: c.addressLine2 ?? "",
  };
}
